/** TWILIO_SMS_NUMBER control: search purchasable SMS numbers (preview), let ops pick one, then
 *  purchase it (explicit, paid step). "If Twilio allows, show a preview of the number before
 *  committing to it" — the search lists numbers and the operator chooses before buying.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem, TwilioSmsDetail, TwilioSmsNumberOption } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { copyOnlyActions } from '../tenantLinks';

export function TwilioSmsNumberControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const detail = item.detail as unknown as TwilioSmsDetail;
  const [areaCode, setAreaCode] = useState('');
  const [numbers, setNumbers] = useState<TwilioSmsNumberOption[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [opError, setOpError] = useState<string | null>(null);

  const search = useMutation({
    mutationFn: () =>
      items.searchTwilioSmsNumbers(topId, item.id, { area_code: areaCode.trim() || undefined }),
    onMutate: () => setOpError(null),
    onSuccess: (res) => {
      if (res.ok) {
        setNumbers(res.numbers ?? []);
        setSelected(null);
        if (!(res.numbers ?? []).length) snackbar.show('No numbers found — try another area code.', 'info');
      } else {
        setOpError(typeof res.detail === 'string' ? res.detail : null);
        snackbar.show(res.reason === 'not_configured' ? 'Twilio is not configured.' : 'Search failed.', 'error');
      }
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Search failed.', 'error'),
  });

  const provision = useMutation({
    mutationFn: () => items.provisionTwilioSmsNumber(topId, item.id, { phone_number: selected ?? '' }),
    onMutate: () => setOpError(null),
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show(`Purchased ${selected}.`, 'success');
        setNumbers([]);
        setSelected(null);
        onChanged();
      } else {
        setOpError(typeof res.detail === 'string' ? res.detail : null);
        snackbar.show('Purchase failed.', 'error');
      }
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Purchase failed.', 'error'),
  });

  if (detail?.configured === false) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">Twilio isn&apos;t configured on the server.</Alert>
      </Box>
    );
  }

  const busy = search.isPending || provision.isPending;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        {opError && (
          <Alert severity="error" onClose={() => setOpError(null)} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {opError}
          </Alert>
        )}
        {detail?.sms_number ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleIcon fontSize="small" color="success" />
            <Typography variant="body2">
              Current SMS number:{' '}
              <ValueActions actions={copyOnlyActions(detail.sms_number, 'Copy SMS number')}>
                <strong>{detail.sms_number}</strong>
              </ValueActions>
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No SMS number provisioned yet.
          </Typography>
        )}

        {canManage && (
          <>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                label="Area code (optional)"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value)}
                disabled={busy}
                sx={{ maxWidth: 180 }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => search.mutate()}
                disabled={busy}
                startIcon={search.isPending ? <CircularProgress size={14} /> : undefined}
              >
                Search numbers
              </Button>
            </Stack>

            {numbers.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Pick a number to purchase:
                </Typography>
                <RadioGroup value={selected ?? ''} onChange={(e) => setSelected(e.target.value)}>
                  {numbers.map((n) => (
                    <FormControlLabel
                      key={n.phone_number}
                      value={n.phone_number}
                      control={<Radio size="small" />}
                      disabled={busy}
                      label={`${n.phone_number}${n.locality ? ` — ${n.locality}, ${n.region ?? ''}` : ''}`}
                    />
                  ))}
                </RadioGroup>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => provision.mutate()}
                  disabled={busy || !selected}
                  startIcon={provision.isPending ? <CircularProgress size={14} /> : undefined}
                >
                  {selected ? `Purchase ${selected}` : 'Purchase selected number'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}
